import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

// API hooks
import { useGetPresentationsQuery } from '../../services/api';

// Redux
import { useSelector } from 'react-redux';
import { RootState } from '../../services/store';
import { Presentation } from '../../services/slices/presentationSlice';

const Presentations: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const isAdmin = user?.attributes?.['custom:role'] === 'admin';
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // Fetch presentations
  const { data: presentations, isLoading, error } = useGetPresentationsQuery(undefined);
  
  // Filter presentations based on tab and search
  const getFilteredPresentations = () => {
    if (!presentations) return [];
    
    let filtered = [...presentations];
    
    // Filter by tab
    if (tabValue === 1) {
      filtered = filtered.filter(p => p.status === 'active');
    } else if (tabValue === 2) {
      filtered = filtered.filter(p => p.status === 'completed');
    } else if (tabValue === 3) {
      filtered = filtered.filter(p => p.status === 'draft');
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        p =>
          p.title.toLowerCase().includes(term) ||
          p.presenter.toLowerCase().includes(term) ||
          p.description.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'info';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Presentations
        </Typography>
        
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/presentations/new')}
          >
            New Presentation
          </Button>
        )}
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search presentations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="presentation tabs">
          <Tab label="All" />
          <Tab label="Active" />
          <Tab label="Completed" />
          <Tab label="Drafts" />
        </Tabs>
      </Box>
      
      {isLoading ? (
        <Typography>Loading presentations...</Typography>
      ) : error ? (
        <Typography color="error">Error loading presentations</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Presenter</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Questions</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredPresentations().map((presentation) => (
                <TableRow key={presentation.id}>
                  <TableCell>{presentation.title}</TableCell>
                  <TableCell>{presentation.presenter}</TableCell>
                  <TableCell>
                    {new Date(presentation.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={presentation.status.charAt(0).toUpperCase() + presentation.status.slice(1)}
                      color={getStatusChipColor(presentation.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{presentation.questions.length}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => navigate(`/presentations/${presentation.id}`)}
                      size="small"
                    >
                      <ViewIcon />
                    </IconButton>
                    
                    {isAdmin && (
                      <>
                        <IconButton
                          onClick={() => navigate(`/presentations/${presentation.id}/edit`)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small">
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                    
                    {presentation.status === 'active' && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate(`/vote/${presentation.id}`)}
                        sx={{ ml: 1 }}
                      >
                        Vote
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              
              {getFilteredPresentations().length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No presentations found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default Presentations;
